import React, { useEffect, useMemo, useRef, useState } from "react";
import Cookies from "js-cookie";
import smartLendLogo from "./img/sl_long_new_logo_wo_beta.png";
import singpassLogo from "./img/singpass_logo.png";
import bankImage from "./img/11.png";
import bankImage1 from "./img/12.png";
import bankImage2 from "./img/13.png";
import bankImage3 from "./img/14.png";
import bankImage4 from "./img/15.png";
import bankImage5 from "./img/16.png";
import bankImage6 from "./img/17.png";
import bankImage7 from "./img/18.png";
import bankImage8 from "./img/19.png";

const IconBadge = ({ icon, label }) => (
  <span className="sl-badge">
    <span className="sl-badgeIcon">{icon}</span>
    <span>{label}</span>
  </span>
);

const SectionHeader = ({ kicker, title, desc, align = "left", right }) => (
  <div className={`sl-sectionHeader sl-align-${align}`}>
    <div>
      {kicker ? <div className="sl-kicker">{kicker}</div> : null}
      <h2 className="sl-sectionTitle">{title}</h2>
      {desc ? <p className="sl-sectionDesc">{desc}</p> : null}
    </div>
    {right ? <div className="sl-sectionRight">{right}</div> : null}
  </div>
);

const FeatureCard = ({ icon, title, desc, meta }) => (
  <article className="sl-featureCard">
    <div className="sl-featureTop">
      <div className="sl-featureIcon">{icon}</div>
      {meta ? <div className="sl-featureMeta">{meta}</div> : null}
    </div>
    <h3 className="sl-featureTitle">{title}</h3>
    <p className="sl-featureDesc">{desc}</p>
  </article>
);

const MarqueeLogos = ({ logos }) => {
  // duplicate list for seamless loop
  const loop = [...logos, ...logos];
  return (
    <div className="sl-marqueeWrap">
      <div className="sl-marqueeFade sl-left" />
      <div className="sl-marqueeFade sl-right" />
      <div
        className="sl-marqueeTrack"
        aria-label="Featured logos scrolling list"
      >
        {loop.map((logo, idx) => (
          <div className="sl-marqueeItem" key={`${idx}-${logo}`}>
            <img
              src={logo}
              alt={`Featured logo ${idx + 1}`}
              style={{ width: "200px" }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);
};

const ALLOWED_LOAN_TERMS = [3, 6, 9, 12];
const FIXED_MONEY_LENDER_INTEREST_RATE_PM = 3.92;

const normalizeLoanTerm = (value) => {
  const numeric = Number(value);
  return ALLOWED_LOAN_TERMS.includes(numeric) ? numeric : 12;
};

const normalizeMonthlyRate = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return FIXED_MONEY_LENDER_INTEREST_RATE_PM;
  return FIXED_MONEY_LENDER_INTEREST_RATE_PM;
};

const LANDING_PAGE_LEAD_ENDPOINT =
  "https://www.smart-lend.com/api/landing-page-leads/upsert";
const LANDING_PAGE_PATH = "/money-lender-campaign";
const LANDING_PAGE_VISITOR_COOKIE = "landing_page_visitor_token";
const LANDING_PAGE_SOURCE_COOKIE = "landing_page_source";
const MONEY_LENDER_CAMPAIGN_COOKIE = "money_lender_campaign_data";
const MONEY_LENDER_CAMPAIGN_DISMISSED_COOKIE =
  "money_lender_campaign_dismissed";

const createVisitorToken = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `lpl-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const MoneyLenderCampaign = () => {
  const [faqCat, setFaqCat] = useState("all");
  const [openFaq, setOpenFaq] = useState(null);
  const [loanAmount, setLoanAmount] = useState(50000);
  const [loanTerm, setLoanTerm] = useState(12);
  const [monthlyRate] = useState(FIXED_MONEY_LENDER_INTEREST_RATE_PM);
  const [estimatedNoa, setEstimatedNoa] = useState("");
  const [hasExistingMoneyLending, setHasExistingMoneyLending] = useState("no");
  const [remainingMoneyLending, setRemainingMoneyLending] = useState("");
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [touched, setTouched] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [showEligibilityPopup, setShowEligibilityPopup] = useState(false);
  const [visitorToken] = useState(() => {
    const existing = Cookies.get(LANDING_PAGE_VISITOR_COOKIE);
    if (existing) {
      return existing;
    }

    const generated = createVisitorToken();
    Cookies.set(LANDING_PAGE_VISITOR_COOKIE, generated, { expires: 365 });
    return generated;
  });
  const saveTimeoutRef = useRef(null);

  const monthlyRepayment = useMemo(() => {
    const monthlyInterestRate = monthlyRate / 100;
    if (monthlyInterestRate === 0) return loanAmount / loanTerm;

    const numerator = loanAmount * monthlyInterestRate;
    const denominator = 1 - Math.pow(1 + monthlyInterestRate, -loanTerm);
    return numerator / denominator;
  }, [loanAmount, loanTerm, monthlyRate]);

  const totalRepayment = monthlyRepayment * loanTerm;
  const totalInterest = totalRepayment - loanAmount;

  const parseCurrencyInput = (value) => {
    const numeric = Number(String(value || "").replace(/[^0-9.]/g, ""));
    return Number.isFinite(numeric) ? numeric : 0;
  };

  const noaAmount = parseCurrencyInput(estimatedNoa);
  const existingLendingAmount =
    hasExistingMoneyLending === "yes"
      ? parseCurrencyInput(remainingMoneyLending)
      : 0;
  const eligibleLoanAmount = Math.max(noaAmount / 3 - existingLendingAmount, 0);
  const roundedEligibleLoanAmount =
    Math.floor(eligibleLoanAmount / 1000) * 1000;

  const phoneIsValid = /^[0-9+\s-]{8,15}$/.test(phone.trim());
  const formIsValid = name.trim().length >= 2 && phoneIsValid && consent;
  const hasEligibilityInputs = noaAmount > 0;

  const eligibilityMessage = !hasEligibilityInputs
    ? "Please provide your estimated NOA so we can assess your borrowing eligibility."
    : loanAmount <= eligibleLoanAmount
      ? `Based on your NOA and current lending profile, you have around a 90% chance of getting your requested amount of ${formatCurrency(loanAmount)}.`
      : `Based on your NOA and current lending profile, you have a higher chance of getting up to ${formatCurrency(roundedEligibleLoanAmount)}.`;

  const highlightedEligibilityAmount = !hasEligibilityInputs
    ? null
    : loanAmount <= eligibleLoanAmount
      ? formatCurrency(loanAmount)
      : formatCurrency(roundedEligibleLoanAmount);

  const eligibilityMessagePrefix = !hasEligibilityInputs
    ? eligibilityMessage
    : loanAmount <= eligibleLoanAmount
      ? "Based on your NOA and current lending profile, you have around a 90% chance of getting your requested amount of"
      : "Based on your NOA and current lending profile, you have a higher chance of getting up to";

  const confettiPieces = useMemo(
    () =>
      Array.from({ length: 36 }, (_, idx) => ({
        id: idx,
        left: Math.random() * 100,
        delay: Math.random() * 0.9,
        duration: 2.4 + Math.random() * 1.8,
        rotate: (Math.random() - 0.5) * 360,
      })),
    [],
  );

  const faqCategories = [
    { key: "all", label: "All" },
    { key: "smartlend", label: "About SmartLend" },
    { key: "borrowing", label: "Borrowing Rules" },
    { key: "safety", label: "Safety & Scams" },
    { key: "rates", label: "Rates" },
  ];

  const faqItems = [
    {
      cat: "borrowing",
      q: "How much can I borrow? What are the legal limits in Singapore?",
      a: (
        <>
          <div className="faq-highlight">
            Your limit depends on your annual income — and it's a combined cap
            across all moneylenders.
          </div>

          <div className="compare-table">
            <div className="compare-row header">
              <div>Annual Income</div>
              <div>Citizens &amp; PRs</div>
              <div>Foreigners in SG</div>
            </div>
            <div className="compare-row">
              <div>Below S$10,000</div>
              <div>S$3,000</div>
              <div>S$500</div>
            </div>
            <div className="compare-row">
              <div>S$10,000 – S$19,999</div>
              <div>S$3,000</div>
              <div>S$3,000</div>
            </div>
            <div className="compare-row">
              <div>S$20,000 and above</div>
              <div>6× monthly income</div>
              <div>6× monthly income</div>
            </div>
          </div>

          <p style={{ marginTop: 12 }}>
            <strong>Important:</strong> These are aggregate limits — your total
            outstanding borrowing across all licensed moneylenders cannot exceed
            this. SmartLend&apos;s lenders check your MLCB record before making
            an offer, so the amount you&apos;re offered already factors in any
            existing loans.
          </p>

          <p style={{ marginTop: 8, fontSize: 12, color: "#7a8499" }}>
            Source: Ministry of Law Singapore —{" "}
            <a
              href="https://rom.mlaw.gov.sg/information-for-borrowers/guide-to-borrowing-from-licensed-moneylenders-english/"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#1a3cbe" }}
            >
              rom.mlaw.gov.sg
            </a>
          </p>
        </>
      ),
    },

    {
      cat: "borrowing",
      q: "What's the maximum interest rate? Are there other fees I should know about?",
      a: (
        <>
          <div className="faq-highlight">
            <span style={{ fontWeight: 800 }}>By law: max</span>
            <span style={{ fontWeight: 300 }}>
              {" "}
              4% per month interest · S$60/month late fee · 10% one-time admin
              fee on principal.
            </span>
          </div>

          <ul>
            <li>
              💰 <strong>Interest rate:</strong> Max 4% per month on outstanding
              balance (reducing basis). Most reputable lenders charge 1–3%.
            </li>
            <li>
              💰 <strong>Admin fee:</strong> One-time, up to 10% of principal,
              deducted upfront. On S$10,000 loan = max S$1,000.
            </li>
            <li>
              💰 <strong>Late repayment interest:</strong> Max 4% per month —
              but only on the overdue amount, not the full loan.
            </li>
            <li>
              💰 <strong>Late repayment fee:</strong> Max S$60 per month, per
              missed instalment.
            </li>
            <li>
              💰 <strong>Legal fees:</strong> Only if lender takes court action
              for a defaulted loan — must be court-ordered.
            </li>
            <li>
              🛡️ <strong>Total charges cap:</strong> All interest, late
              interest, admin fee and late fees combined{" "}
              <strong>cannot exceed the original loan principal</strong>. E.g.
              on a S$10,000 loan, maximum total charges are capped at S$10,000 —
              so you will never repay more than S$20,000 in total.
            </li>
          </ul>

          <p style={{ marginTop: 10 }}>
            Any lender charging beyond these is acting illegally. Report them to
            the Registry of Moneylenders at <strong>1800-2255-529</strong>. All
            SmartLend partner lenders are audited against these legal limits.
          </p>

          <p style={{ marginTop: 8 }}>
            <strong>Watch out for early repayment penalties</strong> — some
            lenders charge a fee if you repay ahead of schedule. Always check
            your contract before signing.
          </p>
        </>
      ),
    },

    {
      cat: "borrowing",
      q: "Will borrowing from a moneylender affect my credit score or bank loan eligibility?",
      a: (
        <>
          <div className="faq-highlight">
            Licensed moneylenders report to MLCB — not CBS. Your bank credit
            score is generally unaffected.
          </div>

          <ul>
            <li>
              🏦 <strong>Credit Bureau Singapore (CBS)</strong> is used by banks
              — licensed moneylenders don't report to CBS, so taking a
              moneylender loan{" "}
              <strong>won't affect your bank loan applications.</strong> <br />
              <strong>Note:</strong> Some banks may ask for your MLCB report if
              you're the sole director or shareholder of a company.
            </li>
            <li>
              📋 <strong>Moneylenders Credit Bureau (MLCB)</strong> — used only
              by licensed moneylenders. All borrowings, repayments and defaults
              are recorded here.
            </li>
            <li>
              ✅ Taking a moneylender loan{" "}
              <strong>will not lower your CBS score</strong>
            </li>
            <li>
              ⚠️ Missing repayments{" "}
              <strong>will appear on your MLCB record</strong>, making future
              moneylender loans harder to get
            </li>
            <li>
              ✅ Responsible repayment can{" "}
              <strong>build a positive MLCB record</strong> over time
            </li>
          </ul>
        </>
      ),
    },

    {
      cat: "safety",
      q: "Is it safe? How do I tell a licensed moneylender from a loan shark?",
      a: (
        <>
          <div className="faq-highlight">
            It&apos;s completely safe — as long as you use a Ministry of
            Law-licensed lender. Here&apos;s how to tell them apart.
          </div>

          <div className="compare-table">
            <div className="compare-row header">
              <div></div>
              <div className="col-sl">Licensed Moneylender</div>
              <div className="col-br">Loan Shark</div>
            </div>
            <div className="compare-row">
              <div>Contacts you first?</div>
              <div className="col-sl">❌ Never (illegal)</div>
              <div className="col-br">✅ SMS/WhatsApp/Telegram</div>
            </div>
            <div className="compare-row">
              <div>Loan contract</div>
              <div className="col-sl">✅ Required by law</div>
              <div className="col-br">❌ None</div>
            </div>
            <div className="compare-row">
              <div>In-person meeting</div>
              <div className="col-sl">✅ Required before disbursement</div>
              <div className="col-br">❌ Fully remote</div>
            </div>
            <div className="compare-row">
              <div>Upfront fee to approve</div>
              <div className="col-sl">❌ Illegal</div>
              <div className="col-br">✅ Common scam</div>
            </div>
            <div className="compare-row">
              <div>Takes your NRIC/ATM card</div>
              <div className="col-sl">❌ Illegal</div>
              <div className="col-br">✅ Common</div>
            </div>
            <div className="compare-row">
              <div>Harassment on default</div>
              <div className="col-sl">❌ Illegal — report to MinLaw</div>
              <div className="col-br">✅ Frequent</div>
            </div>
          </div>

          <p style={{ marginTop: 12 }}>
            All SmartLend lenders are verified against MinLaw&apos;s Registry.
            Cross-check any lender at{" "}
            <a
              href="https://rom.mlaw.gov.sg"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#1a3cbe" }}
            >
              rom.mlaw.gov.sg
            </a>
            . Report harassment to the X-Ah Long hotline:{" "}
            <strong>1800-924-5664</strong>.
          </p>
        </>
      ),
    },

    {
      cat: "borrowing",
      q: "Can I get approved with bad credit, as a self-employed person, or as a foreigner?",
      a: (
        <>
          <div className="faq-highlight">
            Yes — moneylenders are far more flexible than banks. Your ability to
            repay matters more than credit history.
          </div>

          <ul>
            <li>
              ✅ <strong>Bad or no credit history</strong> — assessed
              individually, not auto-rejected by algorithm. Lenders focus on
              current income.
            </li>
            <li>
              ✅ <strong>Self-employed / freelancers</strong> — bank statements
              (3–6 months), CPF history, or IRAS NOA accepted as income proof
            </li>
            <li>
              ✅ <strong>Commission-based earners</strong> — last 3 months&apos;
              payslips or CPF history accepted
            </li>
            <li>
              ✅ <strong>Foreigners on valid work passes</strong> — EP, S Pass,
              Work Permit holders eligible with income proof and valid passport
            </li>

            <li
              style={{
                background: "#fff8ee",
                border: "1px solid #ffd27a",
                borderRadius: 8,
                padding: "10px 12px",
                marginTop: 2,
                listStyle: "none",
              }}
            >
              ⚠️ <strong>Note on foreigner quotas:</strong> Most licensed
              moneylenders have an annual quota on how many foreign nationals
              they can disburse loans to. Depending on the lender, they may
              reject your application or offer a lower loan amount — even if you
              meet all the eligibility criteria. SmartLend matches you to
              lenders who still have available quota, saving you the wasted
              trips.
            </li>

            <li>
              ⚠️ <strong>Unemployed</strong> — some lenders accept consistent
              alternative income (rental, dividends, alimony). Case-by-case.
            </li>
          </ul>

          <p style={{ marginTop: 10 }}>
            SmartLend matches you to lenders with the highest approval rates for
            your specific income type and profile — so you don&apos;t waste time
            or trips on lenders unlikely to approve you.
          </p>
        </>
      ),
    },

    {
      cat: "smartlend",
      q: "Why should I use SmartLend instead of going directly to a moneylender?",
      a: (
        <>
          <div className="faq-highlight">
            You save time, get better offers, and avoid the guesswork — all for
            free.
          </div>

          <p>
            Walking into a moneylender&apos;s office without doing your homework
            puts you at a disadvantage. You have no reference point for
            what&apos;s a fair rate, and you&apos;re negotiating alone.
            SmartLend changes that dynamic entirely.
          </p>

          <ul>
            <li>
              ✅ <strong>One application, multiple offers</strong> — instead of
              visiting multiple offices separately, you get competitive offers
              from our curated lenders sent to you
            </li>
            <li>
              ✅ <strong>Pre-vetted lenders</strong> — we only work with
              MAS-licensed lenders with proven track records and high approval
              rates
            </li>
            <li>
              ✅ <strong>90% offer consistency</strong> — 9 in 10 SmartLend
              users receive the same offer at the branch as shown online. No
              surprises when you show up.
            </li>
            <li>
              ✅ <strong>No wasted trips</strong> — know your odds before making
              the journey in person
            </li>
            <li>
              ✅ <strong>Zero cost to you</strong> — our lenders pay us, not you
            </li>
          </ul>

          <p style={{ marginTop: 10 }}>
            Think of SmartLend as having a knowledgeable friend who&apos;s
            already done the comparison shopping for you.
          </p>
        </>
      ),
    },

    {
      cat: "smartlend",
      q: "Is my personal data safe on SmartLend? Who sees my application?",
      a: (
        <>
          <div className="faq-highlight">
            Your data is encrypted and only shared with lenders you are matched
            to.
          </div>

          <ul>
            <li>
              ✅ All data is <strong>SSL-encrypted</strong> in transit and
              stored securely
            </li>
            <li>
              ✅ Your information is only shared with lenders{" "}
              <strong>after you submit a request</strong> — not before
            </li>
            <li>
              ✅ We do <strong>not</strong> sell or share your data with
              third-party marketers
            </li>
            <li>
              ✅ Our lenders are contractually prohibited from cold-calling or
              harassing you
            </li>
            <li>
              ✅ You can request deletion of your data at any time by contacting
              support
            </li>
          </ul>

          <p style={{ marginTop: 10 }}>
            If you use MyInfo via Singpass, your data is retrieved directly from
            government sources — it does not pass through SmartLend&apos;s
            servers.
          </p>

          <div
            style={{
              background: "#f5f8ff",
              border: "1.5px solid #c5d8ff",
              borderRadius: 10,
              padding: "12px 16px",
              marginTop: 12,
              fontSize: 13,
              color: "#1a2236",
              lineHeight: 1.6,
            }}
          >
            <strong>Don&apos;t have Singpass?</strong> No worries. If
            you&apos;re having trouble signing up or you&apos;re a foreigner
            without Singpass access, simply WhatsApp us and our team will assist
            you manually.
            <br />
            <a
              href="https://wa.me/6587853373"
              target="_blank"
              rel="noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                marginTop: 10,
                background: "#25D366",
                color: "#fff",
                fontWeight: 700,
                fontSize: 13,
                padding: "9px 18px",
                borderRadius: 50,
                textDecoration: "none",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.139.564 4.144 1.546 5.877L0 24l6.293-1.521A11.934 11.934 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.896 0-3.67-.5-5.2-1.373l-.374-.217-3.876.937.979-3.77-.243-.389A9.953 9.953 0 0 1 2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
              </svg>
              WhatsApp +65 8785 3373
            </a>
          </div>
        </>
      ),
    },

    {
      cat: "smartlend",
      q: "Does SmartLend charge a fee? How is it different from a loan broker?",
      a: (
        <>
          <div className="faq-highlight">
            SmartLend is 100% free for borrowers — always.
          </div>

          <p>
            We don&apos;t charge a platform fee, referral fee, or any hidden
            charges at any point. Our mission is to lower the cost of borrowing
            for Singaporeans, and that starts before you even sign a contract.
          </p>

          <p style={{ marginTop: 12 }}>
            Here&apos;s how that stacks up against a traditional loan broker:
          </p>

          <div className="compare-table">
            <div className="compare-row header">
              <div></div>
              <div className="col-sl">SmartLend</div>
              <div className="col-br">Loan Broker</div>
            </div>
            <div className="compare-row">
              <div>Platform / Broker fee</div>
              <div className="col-sl">✅ Free — always</div>
              <div className="col-br">❌ 5–15% of loan</div>
            </div>
            <div className="compare-row">
              <div>Lender choice</div>
              <div className="col-sl">✅ Curated exclusive lenders</div>
              <div className="col-br">❌ Often just 1–2</div>
            </div>
            <div className="compare-row">
              <div>Transparency</div>
              <div className="col-sl">✅ Full offer details upfront</div>
              <div className="col-br">❌ May steer to preferred lender</div>
            </div>
            <div className="compare-row">
              <div>Pressure tactics</div>
              <div className="col-sl">✅ None — you choose</div>
              <div className="col-br">❌ Commission-driven</div>
            </div>
            <div className="compare-row">
              <div>Concierge support</div>
              <div className="col-sl">
                ✅ Dedicated loan concierge to guide you
              </div>
              <div className="col-br">❌ Transactional only</div>
            </div>
          </div>

          <p style={{ marginTop: 12 }}>
            With SmartLend, lenders compete for your request. You compare offers
            side by side and choose on your own terms — no middleman, no markup.
          </p>

          {/* Optional: keep these blocks if your page styling supports them */}
          <div className="concierge-intro">
            <p className="concierge-intro-q">
              But what if you want a dedicated Relationship Manager (RM) to
              guide you every step of the way?
            </p>
            <p className="concierge-intro-body">
              Some of us prefer a human touch — and that&apos;s completely
              valid. For clients who&apos;d rather sit back and let the experts
              handle everything, SmartLend offers a full white-glove concierge
              service. Your dedicated RM takes care of all the negotiations,
              paperwork coordination, and lender communication on your behalf.
              You just tell us what you need, and we do the rest — so you walk
              away with the loan amount you need, stress-free.
            </p>
          </div>

          <div className="concierge-block">
            <div className="concierge-header">
              <div className="concierge-header-icon">✦</div>
              <div className="concierge-title">
                Upgrade to Concierge — Your Fast-Track to Funding
              </div>
              <div className="concierge-sub">
                Enjoy one-on-one RM support, pre-approval checks, and direct
                lender negotiations — all handled for you.
              </div>
            </div>

            <div className="concierge-grid">
              <div className="concierge-feat">
                <div className="cfeat-icon">👤</div>
                <div>
                  <div className="cfeat-title">Dedicated RM &amp; Unified Inbox</div>
                  <div className="cfeat-desc">
                    We liaise with lenders on your behalf so you can focus on
                    your life and await approval stress-free.
                  </div>
                </div>
              </div>

              <div className="concierge-feat">
                <div className="cfeat-icon">✓</div>
                <div>
                  <div className="cfeat-title">Pre-Qualified Loan Matches</div>
                  <div className="cfeat-desc">
                    Say goodbye to generic rejections. We connect you only with
                    lenders you&apos;re eligible for.
                  </div>
                </div>
              </div>

              <div className="concierge-feat">
                <div className="cfeat-icon">⚡</div>
                <div>
                  <div className="cfeat-title">
                    Priority Processing &amp; Faster Turnarounds
                  </div>
                  <div className="cfeat-desc">
                    Jump the queue — your application gets flagged for faster
                    review and faster disbursal.
                  </div>
                </div>
              </div>

              <div className="concierge-feat">
                <div className="cfeat-icon">🔔</div>
                <div>
                  <div className="cfeat-title">
                    Smart Monitoring &amp; Early Refinance Alerts
                  </div>
                  <div className="cfeat-desc">
                    Get notified when you&apos;re eligible for better terms. We
                    track your loan health and flag refinance opportunities
                    early.
                  </div>
                </div>
              </div>
            </div>

            <div className="concierge-cta-row">
              <button className="concierge-btn-outline" type="button">
                Learn more
              </button>
              <button className="concierge-btn-fill" type="button">
                Get Concierge Access →
              </button>
            </div>
          </div>
        </>
      ),
    },

    {
      cat: "rates",
      q: "Why do all the offers have the same interest rate and processing fee?",
      a: (
        <>
          <p>
            We standardize the interest rate and processing fee across every
            lender so you can compare offers fairly. Instead of competing on
            price, our lenders compete on what actually helps you — how quickly
            they fund, how much they&apos;ll lend, tenor flexibility, and
            service.
          </p>
          <p>
            Every rate also sits within the limits set by the Ministry of Law,
            so you&apos;re always protected.
          </p>
        </>
      ),
    },

    {
      cat: "rates",
      q: "I've seen lower rates advertised elsewhere. Why not on SmartLend?",
      a: (
        <>
          <p>
            Some lenders advertise a very low rate to attract borrowers, then
            adjust it upward once you visit the branch and they review your
            documents. We don&apos;t allow that &quot;bait-and-switch&quot;
            here.
          </p>
          <p>
            On SmartLend, the rate shown is the rate the lender commits to
            honour — no surprise increases after you&apos;ve already chosen
            them. A headline rate advertised elsewhere often isn&apos;t the rate
            you actually get once fees and post-assessment changes are added in.
          </p>
        </>
      ),
    },

    {
      cat: "rates",
      q: "Can the rate still change after I pick a lender?",
      a: (
        <>
          <p>
            The rate you see is the committed ceiling — a lender can never start
            higher than it. After you visit the branch and they complete their
            due diligence, there may be room to negotiate the rate down further.
          </p>
          <p>In other words, it only ever moves in your favour.</p>
        </>
      ),
    },

    {
      cat: "rates",
      q: "If every lender charges the same, how do I choose one?",
      a: (
        <p>
          Focus on what actually differs between offers: how fast the lender can
          disburse funds, the loan amount they&apos;re offering, tenor
          flexibility, the documents they require, and their reputation and
          service. These are the things that genuinely affect your borrowing
          experience — not a headline number.
        </p>
      ),
    },

    {
      cat: "rates",
      q: "Are these rates within legal limits?",
      a: (
        <p>
          Yes. All interest rates and fees on SmartLend follow the caps set by
          the Ministry of Law — a maximum of 4% monthly interest and a
          processing fee of up to 10% of the principal. You&apos;ll never be
          quoted above the legal limit on our platform.
        </p>
      ),
    },
  ];

  const filteredFaq = useMemo(() => {
    if (faqCat === "all") return faqItems;
    return faqItems.filter((x) => x.cat === faqCat);
  }, [faqCat, faqItems]);

  const benefitItems = [
    {
      title: "Access to a pool of lenders",
      description:
        "Compare offers from multiple financing partners in one place — choose what best matches your cash-flow.",
    },
    {
      title: "Dedicated support team",
      description:
        "We help you prep documents, match lenders, and push for faster turnaround where possible.",
    },
    {
      title: "Tailored borrowing options",
      description:
        "Guidance based on your profile, repayment horizon, and financial needs.",
    },
  ];

  const promiseItems = [
    {
      title: "Fast, guided financing journey",
      description:
        "From first consultation to lender matching — quick steps, clear updates, less guesswork.",
    },
    {
      title: "Support for challenging profiles",
      description:
        "If your financials are not perfect, we still explore practical routes and structures.",
    },
    {
      title: "No borrower left hanging",
      description:
        "If one option isn’t suitable, we advise the next best route so you always have a plan.",
    },
  ];

  const featuredLogos = [
    bankImage,
    bankImage1,
    bankImage2,
    bankImage3,
    bankImage4,
    bankImage5,
    bankImage6,
    bankImage7,
    bankImage8,
  ];

  const saveLeadSnapshot = async (overrides = {}) => {
    const payload = {
      visitor_token: visitorToken,
      landing_page_path: LANDING_PAGE_PATH,
      name,
      phone_number: phone,
      lead_data: {
        loan_amount: loanAmount,
        loan_term: normalizeLoanTerm(loanTerm),
        monthly_rate: normalizeMonthlyRate(monthlyRate),
        estimated_noa: estimatedNoa,
        has_existing_money_lending: hasExistingMoneyLending,
        remaining_money_lending: remainingMoneyLending,
        step,
        touched,
        contact_submitted: contactSubmitted,
        consent,
        source: LANDING_PAGE_PATH,
      },
      ...overrides,
    };

    try {
      await fetch(LANDING_PAGE_LEAD_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.error("Failed to save landing page lead", error);
    }
  };

  useEffect(() => {
    Cookies.set(LANDING_PAGE_SOURCE_COOKIE, LANDING_PAGE_PATH, { expires: 30 });

    saveLeadSnapshot();
  }, []);

  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      saveLeadSnapshot();
    }, 1200);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    loanAmount,
    loanTerm,
    monthlyRate,
    estimatedNoa,
    hasExistingMoneyLending,
    remainingMoneyLending,
    step,
    name,
    phone,
    consent,
    touched,
    contactSubmitted,
  ]);

  useEffect(() => {
    if (hasExistingMoneyLending === "no" && remainingMoneyLending) {
      setRemainingMoneyLending("");
    }
  }, [hasExistingMoneyLending, remainingMoneyLending]);

  const showEstimate = step === 1 || (step === 2 && contactSubmitted);
  const monthlyLabel = showEstimate
    ? formatCurrency(monthlyRepayment)
    : "Complete details to view";

  const handleApplyNow = () => {
    const eligibleAmountForPrefill =
      hasEligibilityInputs && roundedEligibleLoanAmount > 0
        ? loanAmount <= eligibleLoanAmount
          ? loanAmount
          : roundedEligibleLoanAmount
        : loanAmount;

    const campaignPayload = {
      source: LANDING_PAGE_PATH,
      loan_amount: loanAmount,
      eligible_loan_amount: eligibleAmountForPrefill,
      loan_term: normalizeLoanTerm(loanTerm),
      monthly_rate: normalizeMonthlyRate(monthlyRate),
      estimated_noa: estimatedNoa,
      has_existing_money_lending: hasExistingMoneyLending,
      remaining_money_lending: remainingMoneyLending,
      created_at: new Date().toISOString(),
    };

    Cookies.set(MONEY_LENDER_CAMPAIGN_COOKIE, JSON.stringify(campaignPayload), {
      expires: 30,
      path: "/",
    });
    Cookies.set(LANDING_PAGE_SOURCE_COOKIE, LANDING_PAGE_PATH, {
      expires: 30,
      path: "/",
    });
    Cookies.remove(MONEY_LENDER_CAMPAIGN_DISMISSED_COOKIE, { path: "/" });

    const isAuthenticated = Cookies.get("token");
    const userAccountType = Cookies.get("user_account_type");

    if (isAuthenticated && userAccountType === "borrower") {
      window.location.href = "/b-dashboard";
      return;
    }

    window.location.href = "/auth/signin";
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background:
        "radial-gradient(1200px 500px at 10% 0%, rgba(37, 99, 235, 0.20), transparent 55%), radial-gradient(900px 450px at 90% 10%, rgba(109, 92, 246, 0.18), transparent 55%), linear-gradient(180deg, #f7faff 0%, #ffffff 70%)",
      padding: "44px 18px",
    },
    container: { maxWidth: "80vw", margin: "0 auto" }, // desktop
    topBar: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.90), rgba(255,255,255,0.70))",
      border: "1px solid rgba(219, 230, 251, 0.9)",
      borderRadius: 18,
      padding: "14px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      position: "sticky",
      top: 12,
      zIndex: 20,
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)",
    },

    brandLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      minWidth: 260,
    },
    brandName: { margin: 0, color: "#0f172a", fontWeight: 800, fontSize: 14 },
    brandSub: { margin: 0, color: "#475569", fontSize: 13 },

    chipRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
      justifyContent: "flex-end",
    },
    chip: {
      border: "1px solid rgba(203, 213, 225, 0.9)",
      borderRadius: 999,
      padding: "8px 10px",
      background: "rgba(255,255,255,0.9)",
      color: "#0f172a",
      fontSize: 12,
      fontWeight: 700,
      display: "inline-flex",
      gap: 8,
      alignItems: "center",
    },
    chipDot: (bg) => ({
      width: 8,
      height: 8,
      borderRadius: 999,
      background: bg,
      boxShadow: "0 0 0 3px rgba(15, 23, 42, 0.06)",
    }),

    hero: { marginTop: 18, marginBottom: 18 },
    heroKicker: {
      color: "#2563eb",
      fontWeight: 800,
      letterSpacing: "0.10em",
      textTransform: "uppercase",
      fontSize: 12,
      margin: "0 0 10px",
    },
    heroTitle: {
      fontSize: 44,
      lineHeight: 1.08,
      margin: "0 0 12px",
      color: "#0b1220",
      letterSpacing: "-0.02em",
    },
    heroDesc: {
      margin: 0,
      color: "#475569",
      maxWidth: 780,
      fontSize: 16,
      lineHeight: 1.6,
    },

    layout: {
      display: "grid",
      gridTemplateColumns: "1.1fr 0.9fr",
      gap: 18,
      alignItems: "start",
    },

    card: {
      background:
        "linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.82))",
      borderRadius: 18,
      border: "1px solid rgba(219, 230, 251, 0.95)",
      padding: 20,
      boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      overflow: "hidden",
      position: "relative",
    },

    cardGlow: {
      position: "absolute",
      inset: "-120px -120px auto auto",
      width: 240,
      height: 240,
      borderRadius: 999,
      background:
        "radial-gradient(circle, rgba(37,99,235,0.20), transparent 60%)",
      pointerEvents: "none",
    },

    sectionTitle: { margin: "0 0 10px", color: "#0f172a", fontSize: 18 },
    muted: {
      color: "#64748b",
      fontSize: 13,
      margin: "0 0 12px",
      lineHeight: 1.5,
    },

    stepper: { display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" },
    stepPill: (active) => ({
      borderRadius: 999,
      padding: "8px 10px",
      border: `1px solid ${active ? "rgba(37,99,235,0.45)" : "rgba(203,213,225,0.9)"}`,
      background: active ? "rgba(37,99,235,0.10)" : "rgba(255,255,255,0.85)",
      color: active ? "#1d4ed8" : "#334155",
      fontWeight: 800,
      fontSize: 12,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
    }),
    stepBubble: (active) => ({
      width: 18,
      height: 18,
      borderRadius: 999,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 900,
      color: active ? "#ffffff" : "#0f172a",
      background: active ? "#2563eb" : "rgba(148,163,184,0.35)",
    }),

    fieldLabel: {
      display: "block",
      marginBottom: 8,
      fontWeight: 800,
      color: "#0f172a",
    },
    input: {
      width: "100%",
      padding: "12px 12px",
      borderRadius: 12,
      border: "1px solid rgba(203, 213, 225, 0.95)",
      background: "rgba(255,255,255,0.9)",
      outline: "none",
      fontSize: 14,
      color: "#0f172a",
    },
    select: {
      width: "100%",
      padding: "12px 12px",
      borderRadius: 12,
      border: "1px solid rgba(203, 213, 225, 0.95)",
      background: "rgba(255,255,255,0.9)",
      outline: "none",
      fontSize: 14,
      color: "#0f172a",
      appearance: "none",
    },

    rangeRow: {
      display: "flex",
      gap: 12,
      alignItems: "center",
      marginBottom: 10,
    },
    rangeValue: {
      minWidth: 140,
      textAlign: "right",
      fontWeight: 900,
      color: "#1d4ed8",
    },

    btnPrimary: (enabled = true) => ({
      width: "100%",
      padding: "12px 14px",
      border: "none",
      borderRadius: 12,
      background: enabled
        ? "linear-gradient(135deg, #2563eb 0%, #6d5cf6 100%)"
        : "rgba(147, 197, 253, 1)",
      color: "#ffffff",
      fontWeight: 900,
      cursor: enabled ? "pointer" : "not-allowed",
      boxShadow: enabled ? "0 14px 30px rgba(37,99,235,0.25)" : "none",
      transform: "translateY(0px)",
      transition:
        "transform 140ms ease, box-shadow 140ms ease, opacity 140ms ease",
    }),
    btnGhost: {
      width: "100%",
      padding: "12px 14px",
      borderRadius: 12,
      border: "1px solid rgba(148, 163, 184, 0.9)",
      background: "rgba(255,255,255,0.85)",
      fontWeight: 900,
      cursor: "pointer",
    },

    rightCard: {
      background: "linear-gradient(180deg, #0b1220, #0f172a)",
      color: "#ffffff",
      borderRadius: 18,
      padding: 20,
      border: "1px solid rgba(255,255,255,0.08)",
      position: "sticky",
      top: 92,
      boxShadow: "0 20px 60px rgba(2,6,23,0.35)",
      overflow: "hidden",
    },
    rightGlow: {
      position: "absolute",
      inset: "-160px auto auto -120px",
      width: 300,
      height: 300,
      borderRadius: 999,
      background:
        "radial-gradient(circle, rgba(109,92,246,0.35) 0%, rgba(37,99,235,0.18) 35%, transparent 68%)",
      pointerEvents: "none",
      filter: "blur(0px)",
    },
    estimateBox: {
      background: "rgba(148, 163, 184, 0.15)",
      border: "1px solid rgba(148, 163, 184, 0.18)",
      borderRadius: 14,
      padding: 16,
      marginBottom: 14,
    },
    estimateBig: {
      margin: 0,
      fontSize: 36,
      fontWeight: 950,
      letterSpacing: "-0.02em",
    },
    estimateSub: { margin: "0 0 8px", color: "#cbd5e1", fontSize: 13 },

    miniRow: { margin: "0 0 8px", color: "#cbd5e1", fontSize: 14 },
    disclaimer: {
      marginTop: 14,
      color: "#94a3b8",
      fontSize: 12,
      lineHeight: 1.5,
    },

    sectionCard: {
      marginTop: 18,
      background: "rgba(255,255,255,0.92)",
      borderRadius: 18,
      border: "1px solid rgba(219, 230, 251, 0.95)",
      padding: 20,
      boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
    },
    grid3: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 12,
    },
    tile: {
      borderRadius: 14,
      border: "1px solid rgba(226, 232, 240, 1)",
      padding: 16,
      background: "linear-gradient(180deg, #ffffff, #f8fafc)",
      boxShadow: "0 10px 22px rgba(15, 23, 42, 0.05)",
    },
    tileTitle: {
      marginTop: 0,
      marginBottom: 6,
      color: "#0f172a",
      fontSize: 15,
    },
    tileDesc: { margin: 0, color: "#475569", lineHeight: 1.6, fontSize: 14 },

    logosGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
      gap: 12,
      marginTop: 12,
    },
    logoTile: {
      border: "1px solid rgba(226,232,240,1)",
      borderRadius: 14,
      background: "linear-gradient(180deg, #ffffff, #f8fafc)",
      padding: 14,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 78,
      boxShadow: "0 10px 22px rgba(15, 23, 42, 0.04)",
    },
  };

  return (
    <div style={styles.page}>
      {/* local CSS for nicer sliders + responsive */}
      <style>{`
        * { box-sizing: border-box; }
        .sl-range {
          -webkit-appearance: none;
          width: 100%;
          height: 8px;
          border-radius: 999px;
          background: linear-gradient(90deg, rgba(37,99,235,0.85), rgba(109,92,246,0.75));
          outline: none;
        }
        .sl-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #ffffff;
          border: 2px solid rgba(37,99,235,0.9);
          box-shadow: 0 10px 20px rgba(2, 6, 23, 0.15);
          cursor: pointer;
        }
        .sl-range::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #ffffff;
          border: 2px solid rgba(37,99,235,0.9);
          box-shadow: 0 10px 20px rgba(2, 6, 23, 0.15);
          cursor: pointer;
        }
        .sl-fade {
          animation: slFade 220ms ease-out;
        }
        @keyframes slFade {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0px); }
        }


        .sl-popupOverlay {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, 0.55);
          backdrop-filter: blur(3px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 999;
          padding: 18px;
        }
        .sl-popupCard {
          width: min(860px, 96vw);
          border-radius: 24px;
          border: 1px solid rgba(255,255,255,0.38);
          background: linear-gradient(180deg, #ffffff, #f8fbff);
          box-shadow: 0 30px 90px rgba(15, 23, 42, 0.35);
          padding: 40px 26px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        .sl-popupTitle {
          margin: 0 0 12px;
          font-size: clamp(24px, 4vw, 42px);
          line-height: 1.08;
          letter-spacing: -0.02em;
          color: #0f172a;
          font-weight: 950;
        }
        .sl-popupMessage {
          margin: 0 auto;
          max-width: 760px;
          color: #1e293b;
          font-size: clamp(16px, 2.1vw, 24px);
          line-height: 1.35;
          font-weight: 800;
        }

        .sl-popupAmount {
          display: block;
          margin-top: 12px;
          font-size: clamp(26px, 4.4vw, 48px);
          line-height: 1.05;
          font-weight: 950;
          letter-spacing: -0.02em;
          color: #1d4ed8;
          text-shadow: 0 8px 20px rgba(37, 99, 235, 0.18);
        }

        .sl-popupClose {
          margin-top: 22px;
          border: none;
          border-radius: 12px;
          padding: 12px 18px;
          background: linear-gradient(135deg, #2563eb, #6d5cf6);
          color: #fff;
          font-weight: 900;
          cursor: pointer;
        }
        .sl-popupActions {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 10px;
          margin-top: 22px;
          flex-wrap: wrap;
        }
        .sl-popupApply {
          border: none;
          border-radius: 12px;
          padding: 12px 18px;
          background: linear-gradient(135deg, #0b4ecf, #2563eb);
          color: #fff;
          font-weight: 900;
          cursor: pointer;
        }
        .sl-confettiWrap {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
        }
        .sl-confetti {
          position: absolute;
          top: -14px;
          width: 10px;
          height: 16px;
          border-radius: 3px;
          opacity: 0.86;
          animation-name: slConfettiDrop;
          animation-timing-function: linear;
          animation-iteration-count: 1;
          animation-fill-mode: both;
        }
        @keyframes slConfettiDrop {
          0% { transform: translate3d(0, -20px, 0) rotate(0deg); opacity: 0; }
          8% { opacity: 1; }
          100% { transform: translate3d(0, 120vh, 0) rotate(720deg); opacity: 0.95; }
        }

        @media (max-width: 980px) {
          .sl-layout { grid-template-columns: 1fr !important; }
          .sl-sticky { position: relative !important; top: auto !important; }
          .sl-hero-title { font-size: 36px !important; }
          .sl-grid3 { grid-template-columns: 1fr !important; }
        }

        input:focus, select:focus {
          border-color: rgba(37,99,235,0.55) !important;
          box-shadow: 0 0 0 4px rgba(37,99,235,0.12) !important;
        }
        button:hover { transform: translateY(-1px); }
        button:active { transform: translateY(0px); }
        /* ===== Section variety + richer visuals ===== */

        .sl-kicker{
          font-size:12px;
          font-weight:900;
          letter-spacing:.12em;
          text-transform:uppercase;
          color:#2563eb;
          margin-bottom:8px;
        }
        .sl-sectionTitle{
          margin:0;
          font-size:22px;
          letter-spacing:-.02em;
          color:#0f172a;
        }
        .sl-sectionDesc{
          margin:10px 0 0;
          color:#475569;
          line-height:1.7;
          max-width:780px;
        }
        .sl-sectionHeader{
          display:flex;
          align-items:flex-end;
          justify-content:space-between;
          gap:16px;
          margin-bottom:14px;
        }
        .sl-align-center{ text-align:center; justify-content:center; }
        .sl-align-center .sl-sectionDesc{ margin-left:auto; margin-right:auto; }
        .sl-sectionRight{ display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }

        /* Badges */
        .sl-badge{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding:8px 10px;
          border-radius:999px;
          background:rgba(255,255,255,0.75);
          border:1px solid rgba(203,213,225,0.9);
          box-shadow:0 10px 22px rgba(15,23,42,0.05);
          font-size:12px;
          font-weight:900;
          color:#0f172a;
        }
        .sl-badgeIcon{
          width:22px; height:22px;
          border-radius:10px;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          background:linear-gradient(135deg, rgba(37,99,235,0.22), rgba(109,92,246,0.18));
          border:1px solid rgba(37,99,235,0.20);
        }

        /* Alternate section wrapper styles */
        .sl-sectionTint{
          background: linear-gradient(180deg, rgba(37,99,235,0.06), rgba(109,92,246,0.03));
          border: 1px solid rgba(219,230,251,0.95);
          border-radius: 18px;
          padding: 20px;
          box-shadow: 0 16px 40px rgba(15,23,42,0.06);
        }
        .sl-sectionDark{
          background: radial-gradient(800px 240px at 10% 10%, rgba(109,92,246,0.35), transparent 60%),
                      radial-gradient(700px 240px at 90% 20%, rgba(37,99,235,0.30), transparent 60%),
                      linear-gradient(180deg, #0b1220, #0f172a);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 18px;
          padding: 20px;
          color: #ffffff;
          box-shadow: 0 20px 60px rgba(2,6,23,0.35);
        }
        .sl-sectionDark .sl-kicker{ color: rgba(255,255,255,0.75); }
        .sl-sectionDark .sl-sectionTitle{ color: #ffffff; }
        .sl-sectionDark .sl-sectionDesc{ color: rgba(203,213,225,0.92); }
        .sl-sectionDark .sl-badge{
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.12);
          color: #ffffff;
        }

        /* Feature cards */
        .sl-featureGrid{
          display:grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 12px;
        }
        .sl-featureCard{
          border-radius: 16px;
          padding: 16px;
          background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,250,252,0.90));
          border: 1px solid rgba(226,232,240,1);
          box-shadow: 0 12px 26px rgba(15,23,42,0.06);
          position: relative;
          overflow: hidden;
        }
        .sl-featureCard::after{
          content:"";
          position:absolute;
          inset:-120px auto auto -120px;
          width:220px; height:220px;
          background: radial-gradient(circle, rgba(37,99,235,0.14), transparent 62%);
          border-radius: 999px;
        }
        .sl-featureTop{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          margin-bottom: 10px;
          position: relative;
          z-index: 1;
        }
        .sl-featureIcon{
          width:38px; height:38px;
          border-radius:14px;
          display:flex;
          align-items:center;
          justify-content:center;
          background: linear-gradient(135deg, rgba(37,99,235,0.22), rgba(109,92,246,0.18));
          border: 1px solid rgba(37,99,235,0.18);
          font-size: 18px;
        }
        .sl-featureMeta{
          font-size: 12px;
          font-weight: 900;
          color: #1d4ed8;
          background: rgba(37,99,235,0.10);
          border: 1px solid rgba(37,99,235,0.18);
          padding: 6px 10px;
          border-radius: 999px;
        }
        .sl-featureTitle{
          margin: 0;
          color: #0f172a;
          font-size: 15px;
          position: relative;
          z-index: 1;
        }
        .sl-featureDesc{
          margin: 8px 0 0;
          color: #475569;
          line-height: 1.65;
          font-size: 14px;
          position: relative;
          z-index: 1;
        }

        /* Dark cards variant */
        .sl-sectionDark .sl-featureCard{
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.12);
          box-shadow: none;
        }
        .sl-sectionDark .sl-featureTitle{ color:#ffffff; }
        .sl-sectionDark .sl-featureDesc{ color: rgba(203,213,225,0.92); }
        .sl-sectionDark .sl-featureMeta{
          color:#ffffff;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.14);
        }

        /* Singpass mini-steps */
        .sl-steps{
          display:grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 10px;
          margin-top: 14px;
        }
        .sl-step{
          border-radius: 14px;
          padding: 14px;
          border: 1px solid rgba(226,232,240,1);
          background: rgba(255,255,255,0.75);
          box-shadow: 0 10px 22px rgba(15,23,42,0.05);
        }
        .sl-stepNum{
          width:26px; height:26px;
          border-radius: 10px;
          display:flex;
          align-items:center;
          justify-content:center;
          font-weight: 950;
          color:#ffffff;
          background: linear-gradient(135deg, #2563eb, #6d5cf6);
          margin-bottom: 10px;
        }
        .sl-stepTitle{ margin:0 0 6px; font-weight: 950; color:#0f172a; }
        .sl-stepDesc{ margin:0; color:#475569; line-height:1.65; font-size:13px; }

        /* Marquee */
        .sl-marqueeWrap{
          position: relative;
          border-radius: 18px;
          border: 1px solid rgba(226,232,240,1);
          background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,250,252,0.85));
          overflow: hidden;
          padding: 12px 0;
          box-shadow: 0 16px 40px rgba(15,23,42,0.06);
        }
        .sl-marqueeTrack{
          display:flex;
          gap: 14px;
          width: max-content;
          animation: slMarquee 26s linear infinite;
          padding: 6px 14px;
        }
        .sl-marqueeWrap:hover .sl-marqueeTrack{ animation-play-state: paused; }
        @keyframes slMarquee{
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .sl-marqueeItem{
          min-width: 170px;
          height: 80px;
          display:flex;
          align-items:center;
          justify-content:center;
          border-radius: 14px;
          border: 1px solid rgba(226,232,240,1);
          background: rgba(255,255,255,0.85);
          box-shadow: 0 10px 22px rgba(15,23,42,0.04);
        }
        .sl-marqueeItem img{
          max-width: 200px;
          max-height: 120px;
          object-fit: contain;
          filter: grayscale(12%);
          opacity: 0.95;
        }
        .sl-marqueeFade{
          position:absolute;
          top:0; bottom:0;
          width: 80px;
          z-index: 2;
          pointer-events:none;
        }
        .sl-marqueeFade.sl-left{
          left:0;
          background: linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,0));
        }
        .sl-marqueeFade.sl-right{
          right:0;
          background: linear-gradient(270deg, rgba(255,255,255,1), rgba(255,255,255,0));
        }

        /* Responsive */
        @media (max-width: 980px){
          .sl-featureGrid{ grid-template-columns: 1fr; }
          .sl-steps{ grid-template-columns: 1fr; }
          .sl-sectionHeader{ align-items:flex-start; }
          .sl-sectionRight{ justify-content:flex-start; }
        }
        .concierge-block {
          margin-top: 16px;
        }

        .concierge-header {
          position: relative;
          border-radius: 16px;
          padding: 18px 20px 18px 58px;
          background: linear-gradient(135deg, #3f61b9 0%, #4a76c8 100%);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
          margin-bottom: 16px;
        }

        .concierge-header-icon {
          position: absolute;
          left: 20px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 22px;
          line-height: 1;
          color: #090d15;
          font-weight: 900;
        }

        .concierge-title {
          color: #ffffff;
          font-size: 1.35rem;
          line-height: 1.3;
          font-weight: 800;
          letter-spacing: -0.01em;
        }

        .concierge-sub {
          margin-top: 6px;
          color: rgba(234, 242, 255, 0.95);
          font-size: 1rem;
          line-height: 1.45;
          max-width: 920px;
          font-weight: 500;
        }

        .concierge-grid {
          margin-top: 0;
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 14px;
        }

        .concierge-feat {
          border: 1px solid #d9deed;
          background: #f3f6fc;
          border-radius: 16px;
          padding: 18px;
          display: grid;
          grid-template-columns: 28px 1fr;
          gap: 12px;
          align-items: start;
        }

        .cfeat-icon {
          font-size: 21px;
          line-height: 1;
          margin-top: 1px;
        }

        .cfeat-title {
          color: #112c56;
          font-size: 1.1rem;
          line-height: 1.3;
          font-weight: 800;
          letter-spacing: -0.01em;
          margin-bottom: 6px;
        }

        .cfeat-desc {
          color: #4a6287;
          font-size: 1rem;
          line-height: 1.55;
        }

        .concierge-cta-row {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
        }

        .concierge-btn-outline,
        .concierge-btn-fill {
          min-width: 190px;
          height: 46px;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          padding: 0 20px;
          transition: transform 160ms ease, box-shadow 160ms ease;
        }

        .concierge-btn-outline {
          border: 2px solid #476cc6;
          color: #476cc6;
          background: #ffffff;
        }

        .concierge-btn-fill {
          border: 0;
          color: #ffffff;
          background: linear-gradient(135deg, #3f61b9 0%, #3f61b9 100%);
          box-shadow: 0 8px 22px rgba(63, 97, 185, 0.24);
        }

        @media (max-width: 980px) {
          .concierge-header {
            padding: 14px 14px 14px 48px;
            border-radius: 14px;
          }

          .concierge-header-icon {
            left: 16px;
            font-size: 18px;
          }

          .concierge-title {
            font-size: 1.1rem;
          }

          .concierge-sub,
          .cfeat-desc {
            font-size: 0.95rem;
          }

          .concierge-grid {
            grid-template-columns: 1fr;
          }

          .concierge-feat {
            padding: 14px;
            border-radius: 14px;
            grid-template-columns: 24px 1fr;
            gap: 10px;
          }

          .cfeat-icon {
            font-size: 18px;
          }

          .cfeat-title {
            font-size: 1rem;
          }

          .concierge-btn-outline,
          .concierge-btn-fill {
            width: 100%;
            min-width: 0;
            height: 44px;
            border-radius: 10px;
          }
        }

        /* ===== Mobile: simpler header + wider container ===== */
        @media (max-width: 980px) {
          .sl-container {
            max-width: 95vw !important;
          }

          .sl-topbar {
            position: relative !important;
            top: auto !important;
            padding: 10px 12px !important;
            border-radius: 14px !important;
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }

          /* hide the badge row on mobile */
          .sl-topbar .sl-sectionRight {
            display: none !important;
          }

          /* logo sizing on mobile */
          .sl-topbar img {
            width: 170px !important;
          }
        }

        /* ===== Singpass section: clean mobile layout (keeps all info) ===== */
        @media (max-width: 980px) {
          /* Force singpass section to single column + spacing */
          .sl-singpassGrid {
            grid-template-columns: 1fr !important;
            gap: 12px !important;
          }

          /* Badges become a horizontal scroll row (less wrapping mess) */
          .sl-singpassBadges {
            display: flex !important;
            flex-wrap: nowrap !important;
            overflow-x: auto !important;
            gap: 8px !important;
            padding-bottom: 6px !important;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .sl-singpassBadges::-webkit-scrollbar { display: none; }

          /* Steps become a vertical timeline */
          .sl-steps {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
          .sl-step {
            display: grid !important;
            grid-template-columns: 32px 1fr !important;
            align-items: start !important;
            gap: 10px !important;
            padding: 12px !important;
          }
          .sl-stepNum {
            margin: 0 !important;
            width: 28px !important;
            height: 28px !important;
            border-radius: 12px !important;
          }
          .sl-stepTitle { margin: 0 0 4px !important; }

          /* Feature cards become simple stacked rows (less “card noise”) */
          .sl-singpassFeatures {
            display: grid !important;
            gap: 10px !important;
          }
          .sl-singpassFeatureRow {
            border-radius: 14px;
            border: 1px solid rgba(226,232,240,1);
            background: rgba(255,255,255,0.78);
            box-shadow: 0 10px 22px rgba(15,23,42,0.05);
            padding: 12px;
            display: grid;
            grid-template-columns: 44px 1fr;
            gap: 10px;
            align-items: start;
          }
          .sl-singpassFeatureIcon {
            width: 40px;
            height: 40px;
            border-radius: 14px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: linear-gradient(135deg, rgba(37,99,235,0.22), rgba(109,92,246,0.18));
            border: 1px solid rgba(37,99,235,0.18);
            font-size: 18px;
          }
          .sl-singpassFeatureTop {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 6px;
          }
          .sl-singpassFeatureTitle {
            margin: 0;
            font-weight: 950;
            color: #0f172a;
            font-size: 14px;
          }
          .sl-singpassFeatureMeta {
            font-size: 12px;
            font-weight: 900;
            color: #1d4ed8;
            background: rgba(37,99,235,0.10);
            border: 1px solid rgba(37,99,235,0.18);
            padding: 5px 9px;
            border-radius: 999px;
            white-space: nowrap;
          }
          .sl-singpassFeatureDesc {
            margin: 0;
            color: #475569;
            line-height: 1.6;
            font-size: 13px;
          }
        }
        /* Hide SectionHeader right side on mobile (Singpass section) */
        @media (max-width: 980px) {
          .sl-sectionHeader .sl-sectionRight {
            display: none !important;
          }
        }
        /* =========================
          FAQ (campaign coherent)
          ========================= */
        .sl-faqWrap{
          margin-top: 18px;
        }
        .sl-faqHeader{
          display:flex;
          align-items:flex-end;
          justify-content:space-between;
          gap:14px;
          flex-wrap: wrap;
          margin-bottom: 8px;
        }
        .sl-faqEyebrow{
          font-size:12px;
          font-weight:900;
          letter-spacing:.12em;
          text-transform:uppercase;
          color:#2563eb;
          margin:0 0 8px;
        }
        .sl-faqTitle{
          margin:0;
          font-size:22px;
          letter-spacing:-.02em;
          color:#0f172a;
        }
        .sl-faqDesc{
          margin:10px 0 0;
          color:#475569;
          line-height:1.7;
          max-width:780px;
        }

        /* category pills */
        .sl-faqCats{
          display:flex;
          gap:8px;
          flex-wrap:wrap;
          justify-content:flex-end;
        }
        .sl-faqCat{
          border: 1px solid rgba(203,213,225,0.9);
          background: rgba(255,255,255,0.75);
          padding: 8px 10px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 900;
          color: rgba(15,23,42,0.75);
          cursor: pointer;
          transition: all 150ms ease;
          box-shadow: 0 10px 22px rgba(15,23,42,0.04);
        }
        .sl-faqCat:hover{
          background: rgba(37,99,235,0.08);
          border-color: rgba(37,99,235,0.22);
        }
        .sl-faqCat.active{
          color:#fff;
          border-color: transparent;
          background: linear-gradient(135deg, #2563eb 0%, #6d5cf6 100%);
          box-shadow: 0 14px 30px rgba(37,99,235,0.22);
        }

        /* list */
        .sl-faqList{
          margin-top: 10px;
          display:grid;
          gap:10px;
        }
        .sl-faqItem{
          border-radius: 16px;
          border: 1px solid rgba(226,232,240,1);
          background: linear-gradient(180deg, rgba(255,255,255,0.92), rgba(248,250,252,0.86));
          box-shadow: 0 16px 40px rgba(15,23,42,0.06);
          overflow:hidden;
        }
        .sl-faqQ{
          width:100%;
          border:0;
          background: transparent;
          padding: 14px 14px;
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:12px;
          cursor:pointer;
          text-align:left;
        }
        .sl-faqQText{
          font-weight: 950;
          color:#0f172a;
          font-size: 13px;
          line-height: 1.35;
        }
        .sl-faqIcon{
          width:28px;
          height:28px;
          border-radius: 999px;
          display:grid;
          place-items:center;
          background: rgba(37,99,235,0.10);
          border: 1px solid rgba(37,99,235,0.18);
          color:#1d4ed8;
          font-weight: 950;
          flex: 0 0 auto;
        }
        .sl-faqA{
          max-height:0;
          overflow:hidden;
          transition:max-height 220ms ease;
        }
        .sl-faqA.open{
          max-height: 1200px;
        }
        .sl-faqAInner{
          padding: 0 14px 14px 14px;
        }

        /* inner callouts */
        .sl-faqCallout{
          border-radius: 14px;
          border: 1px solid rgba(37,99,235,0.18);
          background: rgba(37,99,235,0.08);
          padding: 12px;
          color: rgba(15,23,42,0.75);
          font-weight: 800;
          line-height: 1.6;
        }

        /* step list inside faq */
        .sl-faqSteps{
          display:grid;
          gap:10px;
          margin-top: 6px;
        }
        .sl-faqStep{
          display:grid;
          grid-template-columns: 30px 1fr;
          gap:10px;
          align-items:start;
          padding: 10px 12px;
          border-radius: 14px;
          border: 1px solid rgba(226,232,240,1);
          background: rgba(255,255,255,0.8);
        }
        .sl-faqStepDot{
          width:28px; height:28px;
          border-radius: 12px;
          display:flex;
          align-items:center;
          justify-content:center;
          color:#fff;
          font-weight: 950;
          background: linear-gradient(135deg, #2563eb, #6d5cf6);
        }
        .sl-faqStepTitle{
          font-weight: 950;
          color:#0f172a;
          font-size: 13px;
        }
        .sl-faqStepDesc{
          margin-top: 2px;
          color:#475569;
          font-size: 13px;
          line-height: 1.6;
        }

        @media (max-width: 980px){
          .sl-faqCats{ justify-content:flex-start; }
        }
      `}</style>

      {showEligibilityPopup ? (
        <div
          className="sl-popupOverlay"
          role="dialog"
          aria-modal="true"
          aria-label="Eligibility result"
          onClick={() => setShowEligibilityPopup(false)}
        >
          <div className="sl-popupCard" onClick={(e) => e.stopPropagation()}>
            <div className="sl-confettiWrap">
              {confettiPieces.map((piece) => (
                <span
                  key={piece.id}
                  className="sl-confetti"
                  style={{
                    left: `${piece.left}%`,
                    animationDelay: `${piece.delay}s`,
                    animationDuration: `${piece.duration}s`,
                    transform: `rotate(${piece.rotate}deg)`,
                    background:
                      piece.id % 4 === 0
                        ? "#2563eb"
                        : piece.id % 4 === 1
                          ? "#10b981"
                          : piece.id % 4 === 2
                            ? "#f59e0b"
                            : "#6d5cf6",
                  }}
                />
              ))}
            </div>
            <h3 className="sl-popupTitle">You are eligible</h3>
            <p className="sl-popupMessage">
              {eligibilityMessagePrefix}
              {highlightedEligibilityAmount ? (
                <span className="sl-popupAmount">
                  {highlightedEligibilityAmount}
                </span>
              ) : null}
            </p>
            <div className="sl-popupActions">
              <button
                type="button"
                className="sl-popupApply"
                onClick={handleApplyNow}
              >
                Apply now
              </button>
              <button
                type="button"
                className="sl-popupClose"
                onClick={() => setShowEligibilityPopup(false)}
                style={{ marginTop: 0 }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="sl-container" style={styles.container}>
        {/* Top bar */}
        <section className="sl-topbar" style={styles.topBar}>
          <div style={styles.brandLeft}>
            <img
              src={smartLendLogo}
              alt="SmartLend logo"
              style={{ width: 250, maxWidth: "100%", height: "auto" }}
            />
            {/* <div>
              <p style={styles.brandName}>Corporate Financing</p>
              <p style={styles.brandSub}>
                Estimate quickly • Match lenders • Guided advisory
              </p>
            </div> */}
          </div>

          <div className="sl-sectionRight">
            <IconBadge icon="⚡" label="Fast eligibility checks" />
            <IconBadge icon="🤝" label="Lender matching" />
            <IconBadge icon="🔒" label="Singpass only" />
          </div>
        </section>

        {/* Hero */}
        <div style={styles.hero}>
          <p style={styles.heroKicker}>Money Lender Campaign</p>
          <h1 className="sl-hero-title" style={styles.heroTitle}>
            Estimate your borrowing repayment in minutes
          </h1>
          <p style={styles.heroDesc}>
            Adjust loan amount, tenure and indicative rate for a quick estimate.
            Share your contact details and our team will reach out with
            borrowing options that fit your needs and repayment goals.
          </p>
        </div>

        <section className="sl-fade" style={{ marginBottom: 18 }}>
          <div className="sl-sectionTint">
            <SectionHeader
              kicker="Fast approval, fast cash"
              title="Reach multiple money lenders instantly"
              desc="We’ve partnered with the majority of money lenders in Singapore, allowing your request to reach a multitude of lenders instantly for faster responses and payout options."
              right={
                <>
                  <IconBadge icon="⚡" label="Fast approvals" />
                  <IconBadge icon="💸" label="Fast cash" />
                  <IconBadge icon="📨" label="Instant lender reach" />
                </>
              }
            />
          </div>
        </section>

        {/* Main */}
        <div className="sl-layout" style={styles.layout}>
          {/* Left */}
          <section style={styles.card} className="sl-fade">
            <div style={styles.cardGlow} />
            <div style={styles.stepper}>
              <div style={styles.stepPill(step === 1)}>
                <span style={styles.stepBubble(step === 1)}>1</span>
                Estimate + profile
              </div>
              <div style={styles.stepPill(step === 2)}>
                <span style={styles.stepBubble(step === 2)}>2</span>
                Contact details
              </div>
            </div>

            <h2 style={styles.sectionTitle}>
              {step === 1 ? "Setup estimate + profile" : "Your contact details"}
            </h2>
            <p style={styles.muted}>
              {step === 1
                ? "Use the sliders and share a few details so we can route your request better."
                : "We’ll use this to contact you about suitable financing options."}
            </p>

            {step === 1 ? (
              <>
                <label style={styles.fieldLabel}>Loan amount</label>
                <div style={styles.rangeRow}>
                  <input
                    className="sl-range"
                    type="range"
                    min="10000"
                    max="500000"
                    step="5000"
                    value={loanAmount}
                    onChange={(e) => setLoanAmount(Number(e.target.value))}
                  />
                  <div style={styles.rangeValue}>
                    {formatCurrency(loanAmount)}
                  </div>
                </div>

                <label style={styles.fieldLabel}>Loan term</label>
                <div style={{ position: "relative", marginBottom: 14 }}>
                  <select
                    value={loanTerm}
                    onChange={(e) =>
                      setLoanTerm(normalizeLoanTerm(e.target.value))
                    }
                    style={styles.select}
                    aria-label="Loan term in months"
                  >
                    {ALLOWED_LOAN_TERMS.map((term) => (
                      <option key={term} value={term}>
                        {term} months
                      </option>
                    ))}
                  </select>
                </div>

                <label style={styles.fieldLabel}>
                  Indicative interest rate (per month){" "}
                  <span style={{ color: "#1d4ed8", fontWeight: 950 }}>
                    ({monthlyRate.toFixed(2)}% p.m.)
                  </span>
                </label>
                <div style={styles.rangeRow}>
                  <input
                    type="text"
                    value={`${monthlyRate.toFixed(2)}% p.m.`}
                    disabled
                    style={styles.input}
                    aria-label="Fixed monthly interest rate"
                  />
                </div>

                <label style={styles.fieldLabel}>
                  Estimated NOA (latest year)
                </label>
                <input
                  type="text"
                  value={estimatedNoa}
                  onChange={(e) => setEstimatedNoa(e.target.value)}
                  placeholder="e.g. SGD 65,000"
                  style={{ ...styles.input, marginBottom: 12 }}
                />

                <label style={styles.fieldLabel}>
                  Do you currently have a money lending loan?
                </label>
                <select
                  value={hasExistingMoneyLending}
                  onChange={(e) => setHasExistingMoneyLending(e.target.value)}
                  style={{ ...styles.select, marginBottom: 12 }}
                  aria-label="Current money lending status"
                >
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>

                {hasExistingMoneyLending === "yes" ? (
                  <>
                    <label style={styles.fieldLabel}>
                      Remaining money lending amount
                    </label>
                    <input
                      type="text"
                      value={remainingMoneyLending}
                      onChange={(e) => setRemainingMoneyLending(e.target.value)}
                      placeholder="e.g. SGD 12,000"
                      style={{ ...styles.input, marginBottom: 12 }}
                    />
                  </>
                ) : null}

                <div style={{ display: "grid", gap: 10, marginTop: 14 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setStep(2);
                      saveLeadSnapshot({
                        lead_data: {
                          loan_amount: loanAmount,
                          loan_term: normalizeLoanTerm(loanTerm),
                          monthly_rate: normalizeMonthlyRate(monthlyRate),
                          estimated_noa: estimatedNoa,
                          has_existing_money_lending: hasExistingMoneyLending,
                          remaining_money_lending: remainingMoneyLending,
                          step: 2,
                          touched,
                          contact_submitted: contactSubmitted,
                          consent,
                          source: LANDING_PAGE_PATH,
                          event: "continue_to_contact",
                        },
                      });
                    }}
                    style={styles.btnPrimary(true)}
                  >
                    Continue
                  </button>
                  <p
                    style={{
                      margin: 0,
                      color: "#64748b",
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}
                  >
                    Tip: Keep the rate “indicative” — actual offers depend on
                    lender assessment.
                  </p>
                </div>
              </>
            ) : (
              <>
                <label style={styles.fieldLabel}>Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => {
                    const nextName = e.target.value;
                    setName(nextName);
                  }}
                  onBlur={() => saveLeadSnapshot({ name })}
                  placeholder="e.g. Jane Tan"
                  style={{ ...styles.input, marginBottom: 12 }}
                />

                <label style={styles.fieldLabel}>Phone number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    const nextPhone = e.target.value;
                    setPhone(nextPhone);
                  }}
                  onBlur={() => saveLeadSnapshot({ phone_number: phone })}
                  placeholder="e.g. +65 9123 4567"
                  style={{ ...styles.input, marginBottom: 12 }}
                />

                <label
                  style={{ display: "flex", gap: 10, alignItems: "flex-start" }}
                >
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => {
                      const nextConsent = e.target.checked;
                      setConsent(nextConsent);
                    }}
                    style={{ marginTop: 3 }}
                  />
                  <span
                    style={{ color: "#475569", fontSize: 13, lineHeight: 1.55 }}
                  >
                    I agree to share my contact information with SmartLend so
                    the team can reach out and assist me with suitable financing
                    options and financial decision-making.
                  </span>
                </label>

                {touched && !formIsValid ? (
                  <p
                    style={{
                      color: "#b91c1c",
                      marginTop: 10,
                      marginBottom: 0,
                      fontWeight: 700,
                    }}
                  >
                    Please enter a valid name, phone number, and consent.
                  </p>
                ) : null}

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1.5fr",
                    gap: 10,
                    marginTop: 14,
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setStep(1);
                      setContactSubmitted(false);
                      setShowEligibilityPopup(false);
                      saveLeadSnapshot({
                        lead_data: {
                          loan_amount: loanAmount,
                          loan_term: normalizeLoanTerm(loanTerm),
                          monthly_rate: normalizeMonthlyRate(monthlyRate),
                          estimated_noa: estimatedNoa,
                          has_existing_money_lending: hasExistingMoneyLending,
                          remaining_money_lending: remainingMoneyLending,
                          step: 1,
                          touched,
                          contact_submitted: false,
                          consent,
                          source: LANDING_PAGE_PATH,
                          event: "back_to_estimate",
                        },
                      });
                    }}
                    style={styles.btnGhost}
                  >
                    Back
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setTouched(true);
                      if (formIsValid) {
                        setContactSubmitted(true);
                        setShowEligibilityPopup(true);
                        saveLeadSnapshot({
                          name,
                          phone_number: phone,
                          lead_data: {
                            loan_amount: loanAmount,
                            loan_term: normalizeLoanTerm(loanTerm),
                            monthly_rate: normalizeMonthlyRate(monthlyRate),
                            estimated_noa: estimatedNoa,
                            has_existing_money_lending: hasExistingMoneyLending,
                            remaining_money_lending: remainingMoneyLending,
                            step,
                            touched: true,
                            contact_submitted: true,
                            consent,
                            source: LANDING_PAGE_PATH,
                            event: "view_estimate",
                          },
                        });
                        return;
                      }

                      saveLeadSnapshot({
                        lead_data: {
                          loan_amount: loanAmount,
                          loan_term: normalizeLoanTerm(loanTerm),
                          monthly_rate: normalizeMonthlyRate(monthlyRate),
                          estimated_noa: estimatedNoa,
                          has_existing_money_lending: hasExistingMoneyLending,
                          remaining_money_lending: remainingMoneyLending,
                          step,
                          touched: true,
                          contact_submitted: false,
                          consent,
                          source: LANDING_PAGE_PATH,
                          event: "invalid_submit_attempt",
                        },
                      });
                    }}
                    style={styles.btnPrimary(formIsValid)}
                    disabled={!formIsValid}
                  >
                    Check My Eligibility
                  </button>
                </div>

                {contactSubmitted ? (
                  <div
                    style={{
                      marginTop: 12,
                      padding: 12,
                      borderRadius: 14,
                      border: "1px solid rgba(16,185,129,0.25)",
                      background: "rgba(16,185,129,0.08)",
                    }}
                  >
                    <p style={{ margin: 0, color: "#065f46", fontWeight: 900 }}>
                      Details saved — estimate unlocked.
                    </p>
                    <p
                      style={{
                        margin: "6px 0 0",
                        color: "#065f46",
                        fontSize: 13,
                        lineHeight: 1.55,
                      }}
                    >
                      Your eligibility result is shown in the popup above.
                    </p>
                  </div>
                ) : null}
              </>
            )}
          </section>

          {/* Right */}
          <section
            className="sl-sticky sl-fade"
            style={{ ...styles.rightCard, position: "sticky" }}
          >
            <div style={styles.rightGlow} />
            <h2 style={{ marginTop: 0, marginBottom: 10 }}>
              Your estimated repayment
            </h2>

            <div style={styles.estimateBox}>
              <p style={styles.estimateSub}>Estimated monthly repayment</p>
              <p style={styles.estimateBig}>{monthlyLabel}</p>
              <p
                style={{
                  margin: "8px 0 0",
                  color: "#cbd5e1",
                  fontSize: 12,
                  lineHeight: 1.5,
                }}
              >
                Based on {formatCurrency(loanAmount)} over {loanTerm} months at{" "}
                {monthlyRate.toFixed(2)}% p.m.
              </p>
            </div>

            <p style={styles.miniRow}>
              Principal:{" "}
              <strong style={{ color: "#ffffff" }}>
                {formatCurrency(loanAmount)}
              </strong>
            </p>
            <p style={styles.miniRow}>
              Total repayment:{" "}
              <strong style={{ color: "#ffffff" }}>
                {formatCurrency(totalRepayment)}
              </strong>
            </p>
            <p style={styles.miniRow}>
              Total interest:{" "}
              <strong style={{ color: "#ffffff" }}>
                {formatCurrency(totalInterest)}
              </strong>
            </p>

            <p style={styles.disclaimer}>
              Disclaimer: This is an indicative estimate for planning purposes
              only and is not a loan offer, approval decision, or guaranteed
              rate.
            </p>
          </section>
        </div>

        {/* Singpass */}
        {/* Singpass */}
        <section className="sl-sectionTint sl-fade" style={{ marginTop: 18 }}>
          <SectionHeader
            kicker="Onboarding"
            title="Hassle-free Singpass flow"
            desc="Secure onboarding that reduces manual form filling, speeds up verification, and keeps your application consistent."
            right={
              <div className="sl-singpassBadges">
                <IconBadge icon="🧾" label="Less paperwork" />
                <IconBadge icon="✅" label="Cleaner submissions" />
                <IconBadge icon="⏱️" label="Faster onboarding" />
              </div>
            }
          />

          <div
            className="sl-singpassGrid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
              alignItems: "start",
            }}
          >
            {/* Left side */}
            <div>
              <img
                src={singpassLogo}
                alt="Singpass logo"
                style={{
                  width: 200,
                  maxWidth: "100%",
                  height: "auto",
                  marginBottom: 10,
                }}
              />

              <p style={{ margin: 0, color: "#475569", lineHeight: 1.7 }}>
                Use Singpass to bring in your verified details so you spend less
                time on forms and more time choosing the best financing
                structure.
              </p>

              {/* Steps (becomes a vertical timeline on mobile via CSS) */}
              <div className="sl-steps">
                <div className="sl-step">
                  <div className="sl-stepNum">1</div>
                  <div>
                    <div className="sl-stepTitle">Connect</div>
                    <p className="sl-stepDesc">
                      Authenticate securely via Singpass.
                    </p>
                  </div>
                </div>

                <div className="sl-step">
                  <div className="sl-stepNum">2</div>
                  <div>
                    <div className="sl-stepTitle">Verify</div>
                    <p className="sl-stepDesc">
                      Reduce errors with consistent profile information.
                    </p>
                  </div>
                </div>

                <div className="sl-step">
                  <div className="sl-stepNum">3</div>
                  <div>
                    <div className="sl-stepTitle">Submit</div>
                    <p className="sl-stepDesc">
                      Faster packaging so lenders can respond sooner.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side (desktop keeps cards; mobile becomes clean rows) */}
            <div className="sl-singpassFeatures">
              {/* Desktop cards still work, but mobile will look like neat rows because of CSS */}
              <div
                className="sl-featureGrid"
                style={{ gridTemplateColumns: "1fr" }}
              >
                <FeatureCard
                  icon="🛡️"
                  title="Secure by design"
                  desc="We minimise data handling while improving consistency for your application."
                  meta="Trusted flow"
                />
                <FeatureCard
                  icon="📌"
                  title="Less back-and-forth"
                  desc="Cleaner submissions reduce follow-ups and missing-information delays."
                  meta="Fewer revisions"
                />
                <FeatureCard
                  icon="🚀"
                  title="Speed to options"
                  desc="Once the basics are in, we focus on matching lenders and structures."
                  meta="Faster response"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="sl-fade" style={{ marginTop: 18 }}>
          <div className="sl-sectionTint">
            <SectionHeader
              kicker="Benefits"
              title="Why borrowers choose SmartLend"
              desc="A financing journey that feels guided — not confusing. We focus on clarity, speed, and practical outcomes."
              right={
                <>
                  <IconBadge icon="📈" label="Cash-flow friendly" />
                  <IconBadge icon="🧠" label="Real advisory" />
                  <IconBadge icon="🧩" label="Multiple options" />
                </>
              }
            />

            <div className="sl-featureGrid">
              <FeatureCard
                icon="🏦"
                meta="Choice"
                title="Access to a pool of lenders"
                desc="Compare multiple partner options in one place so you can pick what fits your borrowing needs best."
              />
              <FeatureCard
                icon="🧑‍💼"
                meta="Guided"
                title="Dedicated support team"
                desc="We support you through preparation, lender matching, and follow-ups — with clear next steps."
              />
              <FeatureCard
                icon="🧾"
                meta="Tailored"
                title="Tailored borrowing options"
                desc="Advice based on your profile, repayment horizon, and operational cash-flow needs."
              />
            </div>
          </div>
        </section>

        {/* Promise */}
        <section className="sl-sectionDark sl-fade" style={{ marginTop: 18 }}>
          <SectionHeader
            kicker="Our promise"
            title="Speed — with real advisory"
            desc="We don’t just pass your form around. We help you understand the trade-offs and choose a practical route."
            right={
              <>
                <IconBadge icon="🧭" label="Clear direction" />
                <IconBadge icon="🧪" label="Practical routes" />
                <IconBadge icon="📣" label="Transparent updates" />
              </>
            }
          />

          <div className="sl-featureGrid">
            <FeatureCard
              icon="⚡"
              meta="Fast"
              title="Fast, guided financing journey"
              desc="Quick steps, clear checkpoints, and updates so you always know what’s happening."
            />
            <FeatureCard
              icon="🧱"
              meta="Resilient"
              title="Support for challenging profiles"
              desc="Even if financials aren’t perfect, we explore structures and realistic options."
            />
            <FeatureCard
              icon="🔁"
              meta="No dead-ends"
              title="No borrower left hanging"
              desc="If one option doesn’t fit, we guide you to the next best route with clarity."
            />
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <span className="sl-badge">
              <span className="sl-badgeIcon">🧾</span> Better packaging
            </span>
            <span className="sl-badge">
              <span className="sl-badgeIcon">📌</span> Clear requirements
            </span>
            <span className="sl-badge">
              <span className="sl-badgeIcon">🤝</span> Human support
            </span>
          </div>
        </section>

        {/* Featured */}
        <section className="sl-fade" style={{ marginTop: 18 }}>
          <SectionHeader
            kicker="Coverage"
            title="As featured on"
            desc="A few places where SmartLend and borrowing innovation have been discussed."
            right={
              <>
                <IconBadge icon="✨" label="Trusted by borrowers" />
                <IconBadge icon="🗞️" label="Media coverage" />
              </>
            }
            align="left"
          />

          <MarqueeLogos logos={featuredLogos} />

          <p
            style={{
              marginTop: 10,
              marginBottom: 0,
              color: "#64748b",
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            Note: Logos shown are for recognition only and do not imply
            endorsement.
          </p>
        </section>
        {/* FAQ */}
        <section className="sl-fade sl-faqWrap">
          <div className="sl-sectionTint">
            <div className="sl-faqHeader">
              <div>
                <p className="sl-faqEyebrow">FAQ</p>
                <h2 className="sl-faqTitle">Common questions</h2>
                <p className="sl-faqDesc">
                  Quick answers about eligibility, rates, documents, and what
                  happens next.
                </p>
              </div>

              <div className="sl-faqCats" aria-label="FAQ categories">
                {faqCategories.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className={`sl-faqCat ${faqCat === c.key ? "active" : ""}`}
                    onClick={() => {
                      setFaqCat(c.key);
                      setOpenFaq(null);
                    }}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="sl-faqList">
              {filteredFaq.map((item, idx) => {
                const id = `${faqCat}-${idx}`;
                const isOpen = openFaq === id;

                return (
                  <div className="sl-faqItem" key={id}>
                    <button
                      type="button"
                      className="sl-faqQ"
                      onClick={() => setOpenFaq(isOpen ? null : id)}
                      aria-expanded={isOpen}
                    >
                      <span className="sl-faqQText">{item.q}</span>
                      <span className="sl-faqIcon" aria-hidden="true">
                        {isOpen ? "–" : "+"}
                      </span>
                    </button>

                    <div className={`sl-faqA ${isOpen ? "open" : ""}`}>
                      <div className="sl-faqAInner">{item.a}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Bottom spacing */}
        <div style={{ height: 18 }} />
      </div>
    </div>
  );
};

export default MoneyLenderCampaign;
